






































































































































import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import { mixins } from "vue-class-component";

import VuePlotly from "@statnett/vue-plotly";

import { endpoints } from "@/endpoints";
import { eventLoading } from "@/components/sce/EventLoading";
import { fmtFloat, fmtPct } from "./formatters";
import { buildRcaUrl, RcaFilters } from "./filterQueryParams";
import LinkHotspotsMap from "./LinkHotspotsMap.vue";
import HotspotsSelectedExposurePanel from "./HotspotsSelectedExposurePanel.vue";
import PlotlyResizeMixin from "./plotly-resize-mixin";

interface PanelState {
    loading: boolean;
    error: string | null;
    data: any[];
}

function newPanelState(): PanelState {
    return { loading: false, error: null, data: [] };
}

@Component({
    components: { VuePlotly, LinkHotspotsMap, HotspotsSelectedExposurePanel },
})
export default class HotspotsTab extends mixins(PlotlyResizeMixin) {
    @Prop({ required: true }) startDate!: string;
    @Prop({ required: true }) endDate!: string;
    /** Global sidebar filters — narrow hotspot rankings to matching trips. */
    @Prop({ default: () => ({}) }) filters!: RcaFilters;
    /** Whether the Hotspots tab is active — forwarded to the map so it resizes when the
     * tab becomes visible (mapbox-gl mounts at 0×0 if the tab was hidden). */
    @Prop({ default: true }) active!: boolean;

    links: PanelState = newPanelState();
    corridors: PanelState = newPanelState();

    /** Fixed row cap for both hotspot tables. */
    tableRowLimit = 25;

    /** Closed segment most recently clicked on the map (a dot) or in the hotspots table —
     * drives the range-exposure drill-in panel below the map. */
    selectedRow: { link_id: number; road_name: string } | null = null;

    /** Currently-highlighted (bolded) table row. A link selection (link row click or map dot)
     * and a corridor selection (corridor row click or bar click) are mutually exclusive —
     * they mirror the map's single focus, so at most one row is highlighted across the two
     * hotspot tables. */
    selectedLinkId: number | null = null;
    selectedCorridorName: string | null = null;

    /** When the selection came from a map click (a dot) or a Road corridors bar chart click, float the selected row to the top of
     * its table so it surfaces even if it ranked low. Selections made from the tables leave row order untouched. */
    bringSelectedToTop = false;

    fmtFloat = fmtFloat;
    fmtPct = fmtPct;

    mounted() {
        this.fetchAll();
    }

    resizeCharts() {
        this.refitPlotly(["corridorChart"]);
    }

    /** One key over every fetch input so a param change triggers exactly one fetch. */
    private get fetchParamsKey(): string {
        return `${this.startDate}|${this.endDate}|${JSON.stringify(
            this.filters,
        )}`;
    }

    @Watch("fetchParamsKey")
    onParamsChanged() {
        this.fetchAll();
    }

    fetchAll() {
        if (!this.startDate || !this.endDate) return;
        // A new date range / filter set invalidates the previous selection — drop the row
        // highlight before the data swaps in.
        this.selectedLinkId = null;
        this.selectedCorridorName = null;
        this.bringSelectedToTop = false;
        this.fetchPanel(
            this.links,
            endpoints.roadClosureAnalyticsLinkHotspotsUrl,
            { limit: this.tableRowLimit },
        );
        this.fetchPanel(
            this.corridors,
            endpoints.roadClosureAnalyticsRoadHotspotsUrl,
            // Shared bucket: 25 feeds the corridor table (25) and the chart (top 15).
            { limit: this.tableRowLimit },
        );
    }

    fetchPanel(panel: PanelState, url: string, extra: Record<string, any>) {
        panel.loading = true;
        panel.error = null;
        eventLoading.startLoading();
        Vue.axios({
            url: buildRcaUrl(
                url,
                {
                    start_date: this.startDate,
                    end_date: this.endDate,
                    ...extra,
                },
                this.filters,
            ),
        })
            .then((response: any) => {
                panel.data = response.data || [];
            })
            .catch((err: any) => {
                console.error("[RCA Hotspots]", err);
                panel.error = err?.message || "Failed to load.";
                panel.data = [];
            })
            .finally(() => {
                panel.loading = false;
                eventLoading.stopLoading();
            });
    }

    /** A dot clicked on the hotspot map drills that closed segment into the exposure panel
     * and highlights its row in the link hotspots table. */
    onSelectSegment(row: { link_id: number; road_name: string }) {
        this.selectedRow = row;
        this.selectedLinkId = row.link_id;
        this.selectedCorridorName = null;
        // Clicked on the map — float its row to the top of the link table.
        this.bringSelectedToTop = true;
    }

    /** A click on a Closed road segment hotspots row focuses that segment. */
    onLinkRowClick(arg1: any, arg2: any) {
        const item = arg1 && arg1.link_id != null ? arg1 : arg2 && arg2.item;
        if (!item || item.link_id == null) return;
        this.focusSegment(item.link_id);
    }

    /** A click on a Road corridor hotspots row focuses that corridor. */
    onCorridorRowClick(arg1: any, arg2: any) {
        const item = arg1 && arg1.road_name != null ? arg1 : arg2 && arg2.item;
        if (!item || item.road_name == null) return;
        this.focusCorridor(String(item.road_name));
    }

    /** A click on a corridor bar in the "highest closure activity" chart focuses that
     * corridor. The bar's y value is the road_name. Like a map click, this floats the row to
     * the top of the corridor table. */
    onCorridorBarClick(e: any) {
        const roadName = e && e.points && e.points[0] && e.points[0].y;
        if (roadName == null) return;
        this.focusCorridor(String(roadName), { bringToTop: true });
    }

    /** Zoom the map to a single closed segment, scroll the map into view, and drill it
     * into the exposure panel at road-segment (link) grain. */
    focusSegment(linkId: number) {
        this.selectedLinkId = linkId;
        this.selectedCorridorName = null;
        // Table-row click — highlight in place, don't reorder.
        this.bringSelectedToTop = false;
        const map = this.$refs.hotspotsMap as any;
        if (map) {
            map.focusLink(linkId);
            this.scrollMapIntoView(map);
        }
        const panel = this.$refs.exposurePanel as any;
        if (panel) panel.selectLink(linkId);
    }

    /** Zoom the map to a whole road corridor, scroll the map into view, and drill it into
     * the exposure panel at road-name grain. Shared by the corridor table and bar chart. */
    focusCorridor(roadName: string, opts: { bringToTop?: boolean } = {}) {
        this.selectedCorridorName = roadName;
        this.selectedLinkId = null;
        // Bar-chart clicks float the row to the top; corridor-table row clicks leave it in place.
        this.bringSelectedToTop = !!opts.bringToTop;
        const map = this.$refs.hotspotsMap as any;
        if (map) {
            map.focusRoad(roadName);
            this.scrollMapIntoView(map);
        }
        const panel = this.$refs.exposurePanel as any;
        if (panel) panel.selectRoad(roadName);
    }

    /** Bold the selected link's row in the link hotspots table (v-data-table item-class). */
    linkRowClass(item: any): string {
        return this.selectedLinkId != null &&
            item.link_id === this.selectedLinkId
            ? "selected-hotspot-row"
            : "";
    }

    /** Bold the selected corridor's row in the road corridor hotspots table. */
    corridorRowClass(item: any): string {
        return this.selectedCorridorName != null &&
            String(item.road_name) === this.selectedCorridorName
            ? "selected-hotspot-row"
            : "";
    }

    /** Scroll the hotspot map back into view. */
    scrollMapIntoView(map: any) {
        const el = map.$el as HTMLElement | undefined;
        if (!el) return;
        const scroller = el.closest(".rca-shell") as HTMLElement | null;
        if (!scroller || typeof scroller.scrollTo !== "function") {
            if (typeof el.scrollIntoView === "function") {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
            return;
        }
        // Small top clearance so the map card's title isn't flush against the top of the
        // scroll container.
        const clearance = 16;
        const top =
            scroller.scrollTop +
            el.getBoundingClientRect().top -
            scroller.getBoundingClientRect().top -
            clearance;
        scroller.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }

    linkHeaders = [
        { text: "Road segment ID", value: "link_id" },
        { text: "Road", value: "road_name" },
        { text: "Selected days", value: "selected_day_count", align: "end" },
        {
            text: "Closure events",
            value: "closure_event_count_sum",
            align: "end",
        },
        {
            text: "Closure duration (days)",
            value: "active_day_count",
            align: "end",
        },
        {
            text: "Share of days closed",
            value: "active_day_share",
            align: "end",
        },
        {
            text: "Closed-segment km-days",
            value: "closed_length_km_sum",
            align: "end",
        },
        {
            text: "Max consecutive closed days",
            value: "max_consecutive_days_closed",
            align: "end",
        },
    ];

    corridorHeaders = [
        { text: "Road / Corridor", value: "road_name" },
        { text: "Selected days", value: "selected_day_count", align: "end" },
        {
            text: "Closure events",
            value: "closure_event_count_sum",
            align: "end",
        },
        {
            text: "Closure duration (days)",
            value: "active_day_count",
            align: "end",
        },
        {
            text: "Share of days closed",
            value: "active_day_share",
            align: "end",
        },
        {
            text: "Closed segment-days",
            value: "closed_link_count_sum",
            align: "end",
        },
        {
            text: "Closed-segment km-days",
            value: "closed_length_km_sum",
            align: "end",
        },
        {
            text: "Max consecutive closed days",
            value: "max_consecutive_days_closed",
            align: "end",
        },
    ];

    get linkRows() {
        return this.floatToTop(
            this.links.data,
            (r) =>
                this.selectedLinkId != null &&
                r.link_id === this.selectedLinkId,
        );
    }

    get corridorRows() {
        // Table fixed at tableRowLimit; the chart slices its own 15 from the same bucket.
        return this.floatToTop(
            this.corridors.data.slice(0, this.tableRowLimit),
            (r) =>
                this.selectedCorridorName != null &&
                String(r.road_name) === this.selectedCorridorName,
        );
    }

    /** Return ``rows`` with the first row matching ``isSelected`` moved to the front, so the
     * highlighted row surfaces at the top of the table. Returns the original array untouched
     * when nothing matches or the match is already first. */
    floatToTop(rows: any[], isSelected: (r: any) => boolean): any[] {
        // Only reorder when the caller set bringSelectedToTop — i.e. a map dot click or a
        // corridor bar-chart click. Table-row picks leave the row in place.
        if (!this.bringSelectedToTop) return rows;
        const idx = rows.findIndex(isSelected);
        if (idx <= 0) return rows;
        const reordered = rows.slice();
        const [selected] = reordered.splice(idx, 1);
        reordered.unshift(selected);
        return reordered;
    }

    /** Top-15 corridors as a horizontal bar chart, ranked by closure_event_count_sum. Reads the
     * raw fetched bucket (not corridorRows) and draws only the top 15. */
    get corridorChartRows() {
        return [...this.corridors.data]
            .filter((r) => r.closure_event_count_sum != null)
            .sort(
                (a, b) =>
                    (a.closure_event_count_sum || 0) -
                    (b.closure_event_count_sum || 0),
            )
            .slice(-15);
    }

    get corridorChartTraces() {
        const rows = this.corridorChartRows;
        return [
            {
                type: "bar",
                orientation: "h",
                x: rows.map((r) => r.closure_event_count_sum),
                y: rows.map((r) => r.road_name),
                marker: {
                    color: rows.map((r) => r.active_day_count || 0),
                    colorscale: "Blues",
                    reversescale: true,
                    showscale: true,
                    colorbar: {
                        title: "Closure duration (days)",
                        thickness: 12,
                    },
                },
                customdata: rows.map((r) => [
                    r.active_day_count || 0,
                    r.closed_length_km_sum || 0,
                    r.max_consecutive_days_closed || 0,
                ]),
                hovertemplate:
                    "<b>%{y}</b><br>" +
                    "%{x:,.0f} closure events<br>" +
                    "%{customdata[0]:,.0f} days with closure<br>" +
                    "%{customdata[1]:,.0f} closed-segment km-days<br>" +
                    "%{customdata[2]:,.0f} days of continuous closure" +
                    "<extra></extra>",
            },
        ];
    }

    get corridorChartLayout() {
        return {
            dragmode: false,
            height: Math.max(320, this.corridorChartRows.length * 26 + 80),
            margin: { l: 240, r: 60, t: 8, b: 36 },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
            xaxis: { title: "Closure events", gridcolor: "rgba(0,0,0,0.06)" },
            yaxis: { automargin: true, ticksuffix: "  " },
        };
    }
}
